import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  DataGridPremium, GridColDef, GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import {
  Button, CircularProgress, IconButton, MenuItem, TextField, Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import procurementService from "api/procurementService";
import { IProcurementNotice, IProcurementQuery } from "dto/Procurement/IProcurementNotice";
import { plGridLocales } from "utils/muiGridLocales";
import styles from "./styles.module.scss";

// Typy ogłoszeń BZP (Platforma e-Zamówienia). ContractNotice zweryfikowany na API.
const NOTICE_TYPES: { value: string; label: string }[] = [
  { value: "ContractNotice", label: "Ogłoszenie o zamówieniu" },
  { value: "ContractAwardNotice", label: "Ogłoszenie o wyniku postępowania" },
  { value: "ContractCompletionNotice", label: "Ogłoszenie o wykonaniu umowy" },
  { value: "ContractModificationNotice", label: "Ogłoszenie o zmianie umowy" },
];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString("pl-PL") : "—");
const fmtDateTime = (s?: string) =>
  s ? new Date(s).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "—";

const ProcurementModule = () => {
  const today = useMemo(() => new Date(), []);
  const monthAgo = useMemo(() => new Date(Date.now() - 30 * 864e5), []);

  // Filtry (robocze) + zatwierdzone (uruchamiające zapytanie).
  const [noticeType, setNoticeType] = useState("ContractNotice");
  const [dateFrom, setDateFrom] = useState(isoDay(monthAgo));
  const [dateTo, setDateTo] = useState(isoDay(today));
  const [nip, setNip] = useState("");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState<IProcurementQuery>({
    noticeType: "ContractNotice",
    dateFrom: isoDay(monthAgo),
    dateTo: isoDay(today),
    pageSize: 50,
  });

  const [detail, setDetail] = useState<IProcurementNotice | null>(null);

  const { data, isLoading, isError } = procurementService.useNotices(query);

  const rows = useMemo(
    () => (data?.Items ?? []).map((n, i) => ({ id: n.NoticeNumber || String(i), ...n })),
    [data],
  );

  const runSearch = () =>
    setQuery({
      noticeType,
      dateFrom,
      dateTo,
      pageSize: 50,
      nip: nip.trim() || undefined,
      q: q.trim() || undefined,
    });

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "PublicationDate", headerName: "Publikacja", width: 110,
      valueFormatter: (v) => fmtDate(v as string),
    },
    { field: "NoticeNumber", headerName: "Numer ogłoszenia", width: 180 },
    {
      field: "OrganizationName", headerName: "Zamawiający", flex: 1.2, minWidth: 220,
      renderCell: (p: GridRenderCellParams) => {
        const r = p.row as IProcurementNotice;
        return (
          <div className={styles.orgCell}>
            <span className={styles.orgName} title={r.OrganizationName}>{r.OrganizationName || "—"}</span>
            <span className={styles.orgMeta}>
              {r.OrganizationCity ?? ""}{r.OrganizationNationalId ? ` · NIP ${r.OrganizationNationalId}` : ""}
            </span>
          </div>
        );
      },
    },
    {
      field: "OrderObject", headerName: "Przedmiot", flex: 1.6, minWidth: 260,
      renderCell: (p: GridRenderCellParams) => (
        <span className={styles.subject} title={(p.row as IProcurementNotice).OrderObject}>
          {(p.row as IProcurementNotice).OrderObject || "—"}
        </span>
      ),
    },
    { field: "OrderType", headerName: "Rodzaj", width: 100 },
    {
      field: "SubmittingOffersDate", headerName: "Termin ofert", width: 140,
      valueFormatter: (v) => fmtDateTime(v as string),
    },
    {
      field: "__details", headerName: "", width: 50, sortable: false, filterable: false,
      renderHeader: () => null,
      renderCell: (p: GridRenderCellParams) => (
        <Tooltip title="Podgląd ogłoszenia" placement="left">
          <IconButton size="small" onClick={() => setDetail(p.row as IProcurementNotice)}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], []);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>Elektroniczne zamówienia publiczne</h2>
        <span className={styles.subtitle}>
          Ogłoszenia z Biuletynu Zamówień Publicznych (Platforma e-Zamówienia)
        </span>
      </div>

      <div className={styles.filters}>
        <TextField
          select size="small" label="Typ ogłoszenia" value={noticeType}
          onChange={(e) => setNoticeType(e.target.value)} sx={{ minWidth: 240 }}
        >
          {NOTICE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="Od" InputLabelProps={{ shrink: true }}
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <TextField size="small" type="date" label="Do" InputLabelProps={{ shrink: true }}
          value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        <TextField size="small" label="NIP zamawiającego" value={nip}
          onChange={(e) => setNip(e.target.value)} sx={{ width: 170 }} />
        <TextField size="small" label="Fraza (przedmiot/nazwa/CPV)" value={q}
          onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          sx={{ minWidth: 240, flex: 1 }} />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={runSearch}>
          Szukaj
        </Button>
      </div>

      <div className={styles.count}>
        {isLoading ? "Pobieranie…" : isError ? "Błąd pobierania ogłoszeń." : `Znaleziono ${data?.Count ?? 0} ogłoszeń`}
      </div>

      <div className={styles.grid}>
        <DataGridPremium
          localeText={plGridLocales}
          columns={columns}
          rows={rows}
          loading={isLoading}
          disableRowSelectionOnClick
          columnHeaderHeight={34}
          getRowHeight={() => "auto"}
          hideFooterSelectedRowCount
          sx={{
            border: 0,
            "& .MuiDataGrid-cell": { py: 0.75, alignItems: "flex-start" },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          }}
        />
      </div>

      {detail && createPortal(
        <div className={styles.slideOverlay} onClick={() => setDetail(null)}>
          <div className={styles.slide} onClick={(e) => e.stopPropagation()}>
            <div className={styles.slideHead}>
              <div>
                <div className={styles.slideTitle}>{detail.NoticeNumber}</div>
                <div className={styles.slideMeta}>{detail.OrganizationName}</div>
              </div>
              <IconButton onClick={() => setDetail(null)}><CloseIcon /></IconButton>
            </div>
            <div className={styles.slideBody}>
              {detail.HtmlBody
                ? <iframe title="Ogłoszenie" className={styles.noticeFrame} sandbox="" srcDoc={detail.HtmlBody} />
                : <div className={styles.noBody}>Brak treści ogłoszenia.</div>}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {isLoading && rows.length === 0 && (
        <div className={styles.loadingCenter}><CircularProgress size={28} /></div>
      )}
    </div>
  );
};

export default ProcurementModule;
